<template>
  <div class="modal-overflow">
    <div class="row">
      <div class="col-md-12">
        <tabs value="Preview Invoices" tab-content-classes="text-left" :centered="false">
          <tab-pane v-for="(client, index) in uniqueClients" :title="client.name" :key="client.id" centered="false">
            <div class="row" style="margin-bottom: 12px;">
              <div class="col-md-2">
                <strong>On Hold:</strong> {{ client.quickBooksPreferences.onHold | printBool }}
              </div>
              <div class="col-md-2">
                <strong>To Print:</strong> {{ client.quickBooksPreferences.toBePrinted | printBool }}
              </div>
              <div class="col-md-2">
                <strong>To Email: </strong> {{ client.quickBooksPreferences.toBeEmailed | printBool }}
              </div>
              <div class="col-md-4">
                <div class="form-group" style="display: flex;">
                  <label style="margin-right: 10px;">
                    <strong>Invoice Date:</strong>
                  </label>
                  <date-picker
                      name="invoiceDate"
                      v-model="invoiceDate"
                      type="date"
                      placeholder="Invoice Date"
                      :format="constants.DATE.FORMAT.DISPLAY"
                      :value-format="constants.DATE.FORMAT.VALUE"
                      style="width: 175px !important; margin-top: -9px;"
                      :editable=true
                      :clearable=false>
                  </date-picker>
                </div>
              </div>
            </div>
            <div class="row" style="margin-bottom: 12px">
              <div class="col-md-6" style="padding-top: 6px;">
                <img src="static/img/loading-2.gif" alt="loading..." style="margin-right: 5px;" width="20" height="20" :ref="'currentClient_' + index">
                <span v-if="isVerifying">Verifying that {{client.name}} exists in QuickBooks. Please wait (approx 10 - 15 seconds).</span>
                <span :ref="'currentClientMessage_' + index"></span>
              </div>
              <div class="col-md-6">
                <div class="pull-right">
                  <label>Separate Invoices for each Task?</label>
                  <el-tooltip class="item" style="margin-left: 8px;" effect="dark" content="Selecting 'No' will generate one invoice with multiple line items. Selecting 'Yes' will generate multiple invoices with one line item per invoice. NOTE: This setting must be set explicitly for each client." placement="top-start">
                    <img class="icon-help" src="static/img/icon-help.png"/>
                  </el-tooltip>
                  <el-select name="invoiceByLineItem"
                             class="select"
                             size="small"
                             :name="'invoiceByLineItem-' + client.id"
                             style="margin-left: 12px; width: 100px;"
                             placeholder="Y / N"
                             v-validate="modelValidations.invoiceByLineItem"
                             data-vv-as="invoice by line item"
                             v-model="client.invoiceByLineItem">
                    <el-option v-for="option in invoiceByLineItemOptions"
                               class="select"
                               :value="option.value"
                               :label="option.label"
                               :key="option.label">
                    </el-option>
                  </el-select>
                  <small class="text-danger" v-show="errors.has('invoiceByLineItem-' + client.id)">
                    {{ getError('invoiceByLineItem-' + client.id) }}
                  </small>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12">
                <div v-if="client.invoiceByLineItem === true">
                  <div v-for="(task, index) in tasksByClient(client.name).tasks" style="margin-bottom: 12px;">
                    <h6 style="margin-bottom: 2px; display: inline;">Invoice #{{ index + 1 }} - {{ client.name }}{{ maybeAppendCustomerJob(task.job)}}</h6>
                    <img src="static/img/transparent.png" alt="placeholder" style="margin: 0 0 4px 5px;" width="15" height="15" :ref="'invoiceResult_client_' + refName(client.name) + '_taskId_' + task.id">
                    <el-table class="table-striped wrap-whole-words tasks-table"
                              :data="[task]"
                              border
                              row-key="id"
                              :expand-row-keys="expandRowKeys"
                              header-cell-class-name="wrap-words"
                              style="width: 100%; margin-bottom: 5px;">
                      <el-table-column
                        label="Task"
                        :min-width="200">
                        <template v-slot="props">
                          <span>{{ taskNameWithOptionalRepeatCountSuffix(props.row) }}</span>
                        </template>
                      </el-table-column>
                      <el-table-column
                        :min-width="250"
                        label="Description">
                        <template v-slot="props">
                          <div class="flex flex-col">
                            <el-input v-model="props.row.fieldValues.qbItemDescription"
                                      v-if="isInvoiceSubmittal(props.row) || isInvoiceableTask(props.row)"
                                      type="textarea"
                                      style="width:100%;"
                                      v-validate="'required'"
                                      data-vv-name="QuickBooks Item Description"
                                      @focus="setCurrentQbItemDescription(props.row.fieldValues.qbItemDescription)"
                                      @blur="updateQbItemDescription(props.row)"
                            />
                            <template v-else> {{ props.row.fieldValues.qbItemDescription }} </template>
                          </div>
                        </template>
                      </el-table-column>
                      <el-table-column
                        :min-width="80"
                        align="right"
                        label="QB Item Code(s)">
                        <template v-slot="props">
                          <span v-if="hasLinkedTasks(props.row)">
                            {{ joinItemCodes(props.row) }}
                          </span>
                          <span v-else>
                            {{ lodash.get(props.row, 'fieldValues.qbItemCode') }}
                          </span>
                        </template>
                      </el-table-column>
                      <el-table-column
                          :min-width="120"
                          align="right"
                          label="P.O. Number">
                        <template v-slot="props">
                          <template v-if="!isPoRequired(props.row)">No P.O. Required</template>
                          <el-input
                            v-else-if="isInvoiceSubmittal(props.row) || isInvoice(props.row)"
                            placeholder="P.O. Number"
                            v-model="props.row.fieldValues.poNumber"
                            type="text"
                            class="inline-block"
                            style="width: 135px;"
                            @focus="setCurrentPoNumber(props.row.fieldValues.poNumber)"
                            @blur="updatePoNumber(props.row)"
                            data-vv-name="P.O. Number"/>
                        </template>
                      </el-table-column>
                      <el-table-column
                        :min-width="80"
                        label="Unit Cost"
                        align="right">
                        <template v-slot="props">
                          <span v-if="hasLinkedTasks(props.row)">
                            {{ computeLinkedTasksTotal(props.row, 'price') | formatPrice }}
                          </span>
                          <vue-numeric
                            v-else-if="isInvoiceSubmittal(props.row) || isInvoiceableTask(props.row)"
                            currency="$"
                            separator=","
                            :precision="2"
                            :minus="false"
                            name="price"
                            class="fifty-percent el-input__inner dynamic-field-container"
                            v-model="props.row.fieldValues.price"
                            @blur="updatePrice(props.row)"
                            @focus="setCurrentPrice(props.row.fieldValues.price)"
                            style="width: 110px;"
                            placeholder="$0.00" />
                        </template>
                      </el-table-column>
                      <el-table-column
                        :min-width="100"
                        align="right"
                        label="Travel Charge">
                        <template v-slot="props">
                          <span v-if="hasLinkedTasks(props.row)">
                            {{ computeLinkedTasksTotal(props.row, 'travelCharge') | formatPrice }}
                          </span>
                          <vue-numeric
                            v-else-if="isInvoiceSubmittal(props.row) || isInvoiceableTask(props.row)"
                            currency="$"
                            separator=","
                            :precision="2"
                            :minus="false"
                            name="price"
                            class="fifty-percent el-input__inner dynamic-field-container"
                            v-model="props.row.fieldValues.travelCharge"
                            @blur="updateTravelCharge(props.row)"
                            @focus="setCurrentTravelCharge(props.row.fieldValues.travelCharge)"
                            style="width: 110px;"
                            placeholder="$0.00" />
                        </template>
                      </el-table-column>
                      <el-table-column
                        :min-width="80"
                        align="right"
                        label="Total">
                        <template v-slot="props">
                          {{ sumPriceAndTravelCharge(props.row) | formatPrice }}
                        </template>
                      </el-table-column>
                    </el-table>
                  </div>
                  <div class="row" style="margin-top: 12px;">
                    <div class="col-md-12" style="text-align: right">
                      <div style="display: block;">
                        <strong>{{client.name}} - Invoice Subtotal: </strong> {{tasksByClient(client.name).subtotal | formatPrice}}
                      </div>
                      <div v-if="clientMissingPO(client)" style="color: red; display: block;">Client {{ client.name }} requires a P.O. Number. Please add one before creating invoice(s).</div>
                    </div>
                  </div>
                </div>
                <div v-else-if="client.invoiceByLineItem === false">
                  <h6 style="display:inline; margin-bottom: 2px;">Invoice #1 - {{ client.name }}{{ maybeAppendCustomerJob(tasksByClient(client.name).tasks[0].job)}}</h6>
                  <img src="static/img/transparent.png" alt="placeholder" style="margin: 0 0 4px 5px;" width="15" height="15" :ref="'invoiceResult_client_' + refName(client.name) + '_taskId_' + tasksByClient(client.name).tasks[0].id">
                  <el-table
                    :data="tasksByClient(client.name).tasks"
                    style="width: 100%;margin-bottom: 20px;"
                    border
                    row-key="id"
                    :indent="0"
                    header-row-class-name="wrap-words"
                    class="table-striped wrap-whole-words tasks-table"
                    :expand-row-keys="expandRowKeys">
                    <el-table-column
                      label="Task"
                      :min-width="200">
                      <template v-slot="props">
                        <span>{{ taskNameWithOptionalRepeatCountSuffix(props.row) }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column
                      :min-width="250"
                      label="Description">
                      <template v-slot="props">
                        <el-input
                          v-if="isInvoiceSubmittal(props.row) || isInvoiceableTask(props.row)"
                          v-model="props.row.fieldValues.qbItemDescription"
                          type="textarea"
                          style="width:100%;"
                          v-validate="'required'"
                          data-vv-name="QuickBooks Item Description"
                          @focus="setCurrentQbItemDescription(props.row.fieldValues.qbItemDescription)"
                          @blur="updateQbItemDescription(props.row)"
                        />
                        <template v-else> {{ props.row.fieldValues.qbItemDescription}} </template>
                      </template>
                    </el-table-column>
                    <el-table-column
                      :min-width="80"
                      align="right"
                      label="QB Item Code(s)">
                      <template v-slot="props">
                        <span v-if="hasLinkedTasks(props.row)">
                          {{ joinItemCodes(props.row) }}
                        </span>
                        <span v-else>
                          {{ lodash.get(props.row, 'fieldValues.qbItemCode') }}
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column
                      :min-width="120"
                      align="right"
                      label="P.O. Number">
                      <template v-slot="props">
                        <template v-if="!isPoRequired(props.row)">No P.O. Required</template>
                        <el-input
                          v-else-if="isInvoiceSubmittal(props.row) || isInvoice(props.row)"
                          placeholder="P.O. Number"
                          v-model="props.row.fieldValues.poNumber"
                          type="text"
                          class="inline-block"
                          style="width: 135px;"
                          @focus="setCurrentPoNumber(props.row.fieldValues.poNumber)"
                          @blur="updatePoNumber(props.row)"
                          data-vv-name="P.O. Number"/>
                      </template>
                    </el-table-column>
                    <el-table-column
                      :min-width="80"
                      label="Unit Cost"
                      align="right">
                      <template v-slot="props">
                        <span v-if="hasLinkedTasks(props.row)">
                          {{ computeLinkedTasksTotal(props.row, 'price') | formatPrice }}
                        </span>
                        <vue-numeric
                          v-else-if="isInvoiceSubmittal(props.row) || isInvoiceableTask(props.row)"
                          currency="$"
                          separator=","
                          :precision="2"
                          :minus="false"
                          name="price"
                          class="fifty-percent el-input__inner dynamic-field-container"
                          v-model="props.row.fieldValues.price"
                          @blur="updatePrice(props.row)"
                          @focus="setCurrentPrice(props.row.fieldValues.price)"
                          style="width: 110px;"
                          placeholder="$0.00" />
                      </template>
                    </el-table-column>
                    <el-table-column
                      :min-width="100"
                      align="right"
                      label="Travel Charge">
                      <template v-slot="props">
                        <span v-if="hasLinkedTasks(props.row)">
                          {{ computeLinkedTasksTotal(props.row, 'travelCharge') | formatPrice }}
                        </span>
                        <vue-numeric
                          v-else-if="isInvoiceSubmittal(props.row) || isInvoiceableTask(props.row)"
                          currency="$"
                          separator=","
                          :precision="2"
                          :minus="false"
                          name="travelCharge"
                          class="fifty-percent el-input__inner"
                          v-model="props.row.fieldValues.travelCharge"
                          @blur="updateTravelCharge(props.row)"
                          @focus="setCurrentTravelCharge(props.row.fieldValues.travelCharge)"
                          style="width: 110px;"
                          placeholder="$0.00" />
                      </template>
                    </el-table-column>
                    <el-table-column
                      :min-width="80"
                      align="right"
                      label="Total">
                      <template v-slot="props">
                        {{ sumPriceAndTravelCharge(props.row) | formatPrice }}
                      </template>
                    </el-table-column>
                  </el-table>

                  <div class="row" style="margin-top: 12px;">
                    <div class="col-md-12" style="text-align: right">
                        <div style="display: block;">
                          <strong>{{client.name}} - Invoice Subtotal: </strong> {{tasksByClient(client.name).subtotal | formatPrice}}
                        </div>
                        <div v-if="clientMissingPO(client)" style="color: red; display: block;">Client {{ client.name }} requires a P.O. Number. Please add one before creating this invoice.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </tab-pane>
        </tabs>
      </div>
    </div>
    <div class="row" style="margin-top: 15px;">
      <div class="col-md-9">
        <span v-if="isProcessing">
          <img src="static/img/loading-1.gif" style="margin-right: 7px;" width="20" height="20">Your request is being processed. Responses from QuickBooks typically take 10 - 30 seconds. Each unique client adds approx 5 seconds to complete.
        </span>
        <div v-if="createInvoiceErrors.length > 0">
          <h6 class="warning">Invoice Errors</h6>
          <ul>
            <li v-for="error in createInvoiceErrors">{{ error.message }}</li>
          </ul>
        </div>
      </div>
      <div class="col-md-3">
        <div class="pull-right">
          <p-checkbox class="text-left" v-if="!isQuickbooksOffline && customerNotExistsError" v-model="overrideDisabled">
            Attempt to Create Invoice Anyway
          </p-checkbox>
          <button type="button" @click="initTrackProgressAndCreateInvoices" :disabled="createInvoicesBtnDisabled || isProcessing" class="btn btn-fill btn-info pull-right">
            Create Invoice(s)
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatDateForQuickBooks,
  nameToComparableName,
  notifyError,
  notifyWarning,
  notifySuccess,
  sleepSeconds,
  taskNameWithOptionalRepeatCountSuffix
} from 'src/utilities/utils'
import {ptracPatch} from '@/utilities/ptracApi'
import {TabPane, Tabs} from 'src/components/UIComponents'
import {CREATED, OK} from 'http-status-codes'
import {DatePicker, Tooltip} from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import Constants from 'src/utilities/constants'
import VueNumeric from 'vue-numeric'
import {sync} from 'vuex-pathify'
import _ from 'lodash'

export default {
    props: ['data'],
    components: {
      PSwitch,
      TabPane,
      DatePicker,
      Tabs,
      [Tooltip.name]: Tooltip,
      VueNumeric
    },
    computed: {
      invoiceDate: sync('invoices/create/invoiceDate'),
      uniqueClients() {
        return _.uniq(_.map(this.data.tasks, 'job.client'))
      },
      expandRowKeys() {
        const tasksWithChildren = _.filter(this.data.tasks, (task) => task.job.invoiceBy === Constants.INVOICE_BY.MULTIPLE_TASKS)
        return _.map(tasksWithChildren, 'id')
      },
      createInvoicesBtnDisabled() {
        if (this.overrideDisabled) {
          return false
        }

        const missingCustomer = this.customerExistsCount < this.uniqueClients.length

        let missingLineItemSelection = false
        this.uniqueClients.forEach((client) => {
          // check that explicit YES || NO has been selected for each client
          if (client.invoiceByLineItem == null) {
            missingLineItemSelection = true
          }
        })

        return missingCustomer || missingLineItemSelection
      }
    },
    data() {
      return {
        lodash: _,
        constants: Constants,
        isVerifying: true,
        isProcessing: false,
        createInvoiceErrors: [],
        customerExistsCount: 0,
        customerNotExistsError: false,
        overrideDisabled: false,
        isQuickbooksOffline: false,
        today: new Date(),
        localPoNumbers: [],
        modifying: {
          qbItemDescription: null,
          poNumber: null,
          price: 0,
          travelCharge: 0
        },
        invoiceByLineItemOptions: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' }
        ],
        modelValidations: {
          invoiceByLineItem: {
            required: true
          }
        }
      }
    },
    methods: {
      taskNameWithOptionalRepeatCountSuffix,
      isPoRequired(task) {
        return _.get(task, 'job.client.poRequired') === true
      },
      isInvoice(task) {
        return task.name === this.constants.TASKS.NAMES.INVOICE
      },
      isInvoiceSubmittal(task) {
        return task.name === this.constants.TASKS.NAMES.INVOICE_SUBMITTAL
      },
      isInvoiceableTask(task) {
        return _.get(task, 'linkedTask.name') === this.constants.TASKS.NAMES.INVOICE
      },
      joinItemCodes(task) {
        const itemCodes = []

        for (const linkedTask of task.linkedTasks) {
          itemCodes.push(_.get(linkedTask, `fieldValues.qbItemCode`))
        }

        return itemCodes.join(', ')
      },
      computeLinkedTasksTotal(task, fieldKey) {
        let total = 0

        for (const linkedTask of task.linkedTasks) {
          const value = Number(_.get(linkedTask, `fieldValues.${fieldKey}`))
          total += value
        }

        return total
      },
      hasChildren(task) {
        return task.children && task.children.length > 0
      },
      hasLinkedTasks(task) {
        return task.linkedTasks && task.linkedTasks.length > 0
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      refName(name) {
        // trim of job number if included
        if (name.includes(':')) {
          name = name.substring(0, name.indexOf(':'))
        }
        return nameToComparableName(name)
      },
      maybeAppendCustomerJob(job) {
        return job && job.number ? ':' + job.number : ''
      },
      sumPriceAndTravelCharge(task) {
        if (this.hasLinkedTasks(task)) {
          let price = this.computeLinkedTasksTotal(task, 'price')
          let travelCharge = this.computeLinkedTasksTotal(task, 'travelCharge')

          return price + travelCharge
        } else {
          let price = Number(_.get(task, 'fieldValues.price'))

          if (_.get(task, 'fieldValues.travelCharge')) {
            price += Number(_.get(task, 'fieldValues.travelCharge'))
          }

          return price
        }
      },
      clientMissingPO(client) {
        if (client.poRequired) {
          const tasks = _.filter(this.data.tasks, (task) => task.job.client.name === client.name)

          for (const task of tasks) {
            if (!this.localPoNumbers.find(item => item.id === task.id)) {
              return true
            }
          }
        }
        return false
      },
      setCurrentPoNumber(poNumber) {
        this.modifying.poNumber = poNumber
      },
      async updatePoNumber(task) {
        // nothing has changed, ignore the @blur event
        if (this.modifying.poNumber === task.fieldValues.poNumber) {
          return
        }

        if (!task.fieldValues.poNumber) {
          notifyWarning(this, `${task.job.client.name} requires a P.O. Number`)
          return
        }

        const response = await ptracPatch(Constants.ENTITY_ENDPOINTS.TASKS + `/${task.id}/fieldValue`, {
            fieldKey: Constants.ENTITY_FIELDS.TASK.FIELD_VALUES.PO_NUMBER,
            value: task.fieldValues.poNumber
        })

        if (OK !== response.status) {
          notifyError(this, 'Failed to Update P.O. Number')
          return
        }

        this.localPoNumbers.push({
          id: task.id,
          poNumber: task.fieldValues.poNumber
        })
        notifySuccess(this, 'P.O. Number Updated Successfully')
      },
      setCurrentQbItemDescription(qbItemDescription) {
        this.modifying.qbItemDescription = qbItemDescription
      },
      // this function accepts both a PtracTask or an Elasticsearch task search result
      async updateQbItemDescription(task) {
        // nothing has changed, ignore the @blur event
        if (this.modifying.qbItemDescription === task.fieldValues.qbItemDescription) {
          return
        }

        // Linked Task
        const response = await ptracPatch(Constants.ENTITY_ENDPOINTS.TASKS + `/${task.id}/fieldValue`, {
            fieldKey: Constants.ENTITY_FIELDS.TASK.FIELD_VALUES.QB_ITEM_DESCRIPTION,
            value: task.fieldValues.qbItemDescription
        })

        if (OK !== response.status) {
          notifyError(this, 'Failed to update QB Item Description')
          return
        }

        notifySuccess(this, 'QB Item Description updated successfully')
      },
      setCurrentPrice(price) {
        this.modifying.price = price
      },
      async updatePrice(task) {
        // no change, ignore @blur event
        if (this.modifying.price === task.fieldValues.price) {
          return
        }

        const response = await ptracPatch(Constants.ENTITY_ENDPOINTS.TASKS + `/${task.id}/fieldValue`, {
          fieldKey: Constants.ENTITY_FIELDS.TASK.FIELD_VALUES.PRICE,
          value: task.fieldValues.price
        })

        if (OK !== response.status) {
          notifyError(this, 'Failed to update price')
          return
        }

        notifySuccess(this, 'Price updated successfully')
      },
      setCurrentTravelCharge(travelCharge) {
        this.modifying.travelCharge = travelCharge
      },
      async updateTravelCharge(task) {
        // nothing has changed, ignore the @blur event
        if (this.modifying.travelCharge === task.fieldValues.travelCharge) {
          return
        }

        const response = await ptracPatch(Constants.ENTITY_ENDPOINTS.TASKS + `/${task.id}/fieldValue`, {
          fieldKey: Constants.ENTITY_FIELDS.TASK.FIELD_VALUES.TRAVEL_CHARGE,
          value: task.fieldValues.travelCharge
        })

        if (OK !== response.status) {
          notifyError(this, 'Failed to update travel charge')
          return
        }

        notifySuccess(this, 'Travel charge updated successfully')
      },
      localPoNumber(taskId) {
        const localPoNumber = this.localPoNumbers.find(item => item.id === taskId)
        if (localPoNumber) {
          return localPoNumber.poNumber
        }
        return null
      },
      tasksByClient(clientName) {
        const tasks = _.filter(this.data.tasks, (task) => task.job.client.name === clientName)

        let subtotal = 0;
        tasks.forEach((task) => {
          if (task.linkedTasks) {
            for (const linkedTask of task.linkedTasks) {
              const price = Number(linkedTask.fieldValues.price)
              const travelCharge = Number(linkedTask.fieldValues.travelCharge || 0)
              subtotal += price
              if (travelCharge > 0) {
                subtotal += travelCharge
              }
            }
          } else {
            const price = Number(task.fieldValues.price)
            const travelCharge = Number(task.fieldValues.travelCharge || 0)
            subtotal += price
            if (travelCharge > 0) {
              subtotal += travelCharge
            }
          }
        })

        return {
          tasks,
          subtotal
        }
      },
      async clientExistsInQuickBooks(clientName, index) {
        const response = await this.$store.dispatch('quickbooks/customerExists', clientName)

        let image = 'active-error.png'
        let msg = 'QuickBooks is offline'
        if (OK === response.status) {
          const customerExists = response.data
          if (customerExists.quickbooksOnline) {
            if (customerExists.exists) {
              image = 'active-true.png'
              msg = clientName + ' exists in QuickBooks'
              this.customerExistsCount++
            } else {
              image = 'active-false.png'
              msg = `${clientName} does not exist in QuickBooks. Please add ${clientName} as a customer prior to generating an invoice.`
              this.customerNotExistsError = true
            }
          } else {
            this.isQuickbooksOffline = true
          }
        }

        if (this.$refs['currentClient_' + index]) {
          this.$refs['currentClient_' + index][0].src = 'static/img/' + image
        }
        if (this.$refs['currentClientMessage_' + index]) {
          this.$refs['currentClientMessage_' + index][0].innerHTML = msg
        }

        this.isVerifying = false
      },
      checkForSingleTaskPerClient(client) {
        const tasks = _.filter(this.data.tasks, (task) => task.job.client.name === client.name)

        if (Array.isArray(tasks) && tasks.length === 1) {
          client.invoiceByLineItem = false
        }
      },
      initTrackProgressAndCreateInvoices() {
        this.initTrackProgress()
        this.createInvoices()
      },
      async initTrackProgress() {
        this.isProcessing = true
        this.setAllClientProgressImagesTo('loading-1.gif')
        while (this.isProcessing) {
          await this.trackProgress()
          await sleepSeconds(Constants.TIME.SECONDS.FIVE)
          console.log('initTrackProgress after sleep', new Date().getTime())
        }
      },
      async trackProgress() {
        const progressResponse = await this.$store.dispatch('invoices/create/fetchInvoicesFromPtrac')

        if (OK === progressResponse.status) {
          this.updateProgressIcons(progressResponse.data)
        }
      },
      updateProgressIcons(invoices) {
        for (const invoice of invoices) {
          if (invoice.refNumber) {
            const firstLineItem = invoice.lineItems[0]
            const refName = 'invoiceResult_client_' + this.refName(invoice.customerName) + '_taskId_' + firstLineItem.invoiceSubmittalTaskId
            if (this.$refs[refName]) {
              this.$refs[refName][0].src = 'static/img/active-true.png'
            } else {
              console.error(`Failed to find ref for ${refName}`)
            }
          }
        }
      },
      endProgress() {
        this.isProcessing = false
      },
      setAllClientProgressImagesTo(imageName) {
        for (const property in this.$refs) {
          if (property.startsWith('invoiceResult')) {
            this.$refs[property][0].src = 'static/img/' + imageName
          }
        }
      },
      async createInvoices() {
        this.createInvoiceErrors = []

        const response = await this.$store.dispatch('invoices/create/save', this.data.tasks)

        if (CREATED === response.status) {
          // wait for all invoice submittal tasks and the UI to be updated,
          // then close modal and refresh invoice submittal task search results a moment later
          await sleepSeconds(Constants.TIME.SECONDS.ONE)
          await this.trackProgress()
          notifySuccess(this, 'Invoice(s) created successfully')
          await sleepSeconds(Constants.TIME.SECONDS.TWO)
          this.endProgress();
          this.$eventHub.$emit('invoicesSaved')
          this.$emit('close')
          return
        }
        this.endProgress();
        this.createInvoiceErrors = response.data

        notifyError(this, 'Failed to create invoice(s)', response.data.errors)

        if (Constants.ERROR_TYPES.AXIOS_TIMEOUT === response.code) {
          notifyError(this, 'Request limit of 10 minutes reached. Reloading in 3 seconds.')
          setTimeout(() => {
            // https://stackoverflow.com/questions/41301099/do-we-have-router-reload-in-vue-router
            this.$router.go() // leave go() with zero args to trigger a page reload
          }, Constants.TIME.MILLIS.THREE_SECONDS)
        }
      }
    },
    mounted() {
      this.$store.commit('invoices/create/resetInvoicesClients')
      this.customerNotExistsError = false
      for (let i = 0; i < this.uniqueClients.length; i++) {
        this.clientExistsInQuickBooks(this.uniqueClients[i].name, i)
        this.checkForSingleTaskPerClient(this.uniqueClients[i])
      }

      for (const task of this.data.tasks) {
        if (task.fieldValues.poNumber) {
          this.localPoNumbers.push({
            id: task.id,
            poNumber: task.fieldValues.poNumber
          })
        }
      }
    },
    destroyed() {
      this.$store.set('invoices/create/invoiceDate', formatDateForQuickBooks(new Date()))
    }
  }
</script>

<style scoped>
  .el-table >>> .wrap-words div.cell {
    word-break: keep-all;
  }
  .tasks-table >>> .el-textarea__inner {
    field-sizing: content;
  }
</style>
